import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Edit, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'

type PutUpdateAuthProps = {
  id: string
  applicationId: string
  subId: string | null
  clientKey: string
  secretData: string
  headers: object
  metadata: object | null
}

async function putUpdateAuth({ id, ...data }: PutUpdateAuthProps) {
  return api.backend.put(`/application-auth/${id}`, data)
}

export function useUpdateApplicationAuth() {
  const { t } = useTranslation('sso')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PutUpdateAuthProps
  >({
    mutationKey: ['/application-auth/update'],
    mutationFn: putUpdateAuth,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: Edit,
          title: t(
            'applications.drawer_manage_auth.toast_update_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/application-auth/findOne'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('applications.drawer_manage_auth.toast_update_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('applications.drawer_manage_auth.toast_update_error.description'),
        },
      })
    },
  })

  const handleUpdateAuth = async (data: PutUpdateAuthProps) => {
    return mutateAsync(data)
  }

  return {
    handleUpdateAuth,
    isUpdatingAuth: isPending,
  }
}
